import type { ReactNode } from 'react';

import { useReachEntitlement } from './entitlement';

import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Hiding the sidebar row is not a gate — the URL still works. Reach is a
 * workspace-scoped module (UX PRD §7.3), so every Reach route renders through
 * here.
 *
 * This is the client half. It is honest about what it is: it stops a person who
 * has no business on this surface from wandering onto a half-configured page,
 * and it is NOT a security boundary. The security boundary is RLS, which scopes
 * every `reach_*` row to the caller's own workspace regardless of what the UI
 * lets them open, plus `assertReachEntitled` on the server routes.
 *
 * The empty state says WHY rather than pretending the route does not exist —
 * a 404 for a page that plainly exists in the nav of other workspaces is the
 * kind of lie that costs a support conversation.
 */
export function ReachEntitledRoute({ children }: { children: ReactNode }) {
  const { enabled, isLoading } = useReachEntitlement();

  if (isLoading) {
    return (
      <div className="space-y-4 p-6" aria-busy="true">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <Empty className="border-0">
        <EmptyHeader>
          <EmptyTitle>Reach isn&apos;t enabled for this workspace</EmptyTitle>
          <EmptyDescription>
            Reach is turned on per workspace. This one doesn&apos;t have it, so there&apos;s nothing
            here to show — switch to a workspace that does, or ask for it to be enabled.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return <>{children}</>;
}
